import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  useManufacturerQualityTiers,
  useSetRating,
  type ManufacturerQualityTier,
} from '@/api/quality-tiers'
import {
  MANUFACTURER_RATING_KINDS,
  MANUFACTURER_RATINGS,
  type ManufacturerRatingKind,
} from '@/lib/manufacturer-ratings'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RatingCommentsButton } from '@/features/quality-tiers/rating-comments'
import { RatingSelect } from '@/features/quality-tiers/rating-select'
import { TierPeers } from '@/features/quality-tiers/tier-peers'
import { cn } from '@/lib/utils'

/** The nearest parent's value of one rating, which a category without its own takes. */
function inheritedRating(
  row: ManufacturerQualityTier,
  rows: ManufacturerQualityTier[],
  rating: ManufacturerRatingKind,
) {
  let best: { depth: number; value: string; from: string } | null = null
  for (const other of rows) {
    const value = other[rating]
    if (!value || other.path.length >= row.path.length) continue
    if (!other.path.every((part, index) => row.path[index] === part)) continue
    if (!best || other.path.length > best.depth) {
      best = { depth: other.path.length, value, from: other.path.at(-1) ?? '' }
    }
  }
  return best
}

/**
 * Sales' ratings of the manufacturer in each store category it sells in: Good / Better / Best
 * (USA-1258), Preference and Pricing (USA-1272). A department's rating carries to its categories
 * unless a category sets its own.
 */
export function QualityTiersPanel({
  manufacturerUuid,
  manufacturerName,
}: {
  manufacturerUuid: string
  manufacturerName: string
}) {
  const { data: rows, isLoading } = useManufacturerQualityTiers(manufacturerUuid)
  const update = useSetRating()

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-10" />
        ))}
      </div>
    )
  }
  if (!rows?.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No products in store categories yet
      </p>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Products</TableHead>
          {MANUFACTURER_RATING_KINDS.map((rating) => (
            <TableHead key={rating} className="hidden md:table-cell">
              {MANUFACTURER_RATINGS[rating].label}
            </TableHead>
          ))}
          <TableHead className="w-px">
            <span className="sr-only">Comments</span>
          </TableHead>
          <TableHead className="hidden xl:table-cell">Others ranked here</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const name = row.path.slice(Math.min(row.path.length - 1, 2)).join(' > ')
          const ratings = Object.fromEntries(
            MANUFACTURER_RATING_KINDS.map((rating) => [
              rating,
              { own: row[rating] ?? null, inherited: row[rating] ? null : inheritedRating(row, rows, rating) },
            ]),
          ) as Record<
            ManufacturerRatingKind,
            { own: string | null; inherited: ReturnType<typeof inheritedRating> }
          >
          const select = (rating: ManufacturerRatingKind) => (
            <RatingSelect
              rating={rating}
              value={ratings[rating].own}
              inherited={ratings[rating].inherited}
              context={`${manufacturerName} in ${name}`}
              pending={
                update.isPending &&
                update.variables?.manufacturerUuid === manufacturerUuid &&
                update.variables.nodeId === row.taxonomy_node_id &&
                update.variables.rating === rating
              }
              onChange={(value) =>
                update.mutate(
                  { manufacturerUuid, nodeId: row.taxonomy_node_id, rating, value },
                  {
                    onError: () =>
                      toast.error(
                        `Could not save the ${name} ${MANUFACTURER_RATINGS[rating].label.toLowerCase()} for ${manufacturerName}`,
                      ),
                  },
                )
              }
            />
          )
          return (
            <TableRow
              key={row.taxonomy_node_id}
              className={row.path.length === 1 ? 'bg-muted/40' : undefined}
            >
              <TableCell
                className={cn(
                  'whitespace-normal',
                  row.path.length === 1 && 'font-semibold',
                  row.path.length === 2 && 'pl-6 sm:pl-8',
                  row.path.length > 2 && 'pl-9 sm:pl-12',
                )}
              >
                <Link
                  to="/quality-tiers"
                  search={{ category: row.taxonomy_node_id }}
                  className="hover:underline"
                >
                  {name}
                </Link>
                <p className="text-xs font-normal text-muted-foreground tabular-nums sm:hidden">
                  {row.live_products.toLocaleString()} products
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-normal md:hidden">
                  {MANUFACTURER_RATING_KINDS.map((rating) => (
                    <span key={rating} className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">{MANUFACTURER_RATINGS[rating].label}</span>
                      {select(rating)}
                    </span>
                  ))}
                </div>
                <TierPeers peers={row.others ?? []} className="mt-1 font-normal xl:hidden" />
              </TableCell>
              <TableCell className="hidden text-right tabular-nums text-muted-foreground sm:table-cell">
                {row.live_products.toLocaleString()}
              </TableCell>
              {MANUFACTURER_RATING_KINDS.map((rating) => (
                <TableCell key={rating} className="hidden w-px whitespace-nowrap md:table-cell">
                  {select(rating)}
                </TableCell>
              ))}
              <TableCell className="w-px align-top md:align-middle">
                <RatingCommentsButton
                  nodeId={row.taxonomy_node_id}
                  manufacturerUuid={manufacturerUuid}
                  manufacturerName={manufacturerName}
                  categoryName={name}
                  count={row.comment_count}
                  renderRating={select}
                />
              </TableCell>
              <TableCell className="hidden whitespace-normal xl:table-cell">
                <TierPeers peers={row.others ?? []} />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
